import React from "react";
import { Link } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";

export function DashboardPageShell({ children }) {
  return (
    <div className="dashboard-theme tailwind-container w-full bg-[#f1f4f8] px-3 pb-10 pt-2 text-gray-900 sm:px-4">
      <div className="mx-auto w-full max-w-[1600px]">{children}</div>
    </div>
  );
}

export function DashboardGrid({ children }) {
  return <div className="dashboard-kpi-grid">{children}</div>;
}

function formatCount(value) {
  if (value === "" || value === null || value === undefined) return "—";
  return value;
}

function CardHalfTop({ title, value, hint, subLabel, asLink, to }) {
  const inner = (
    <>
      <p className="dashboard-classic-card__title">{title}</p>
      {subLabel ? <p className="dashboard-classic-card__sub">{subLabel}</p> : null}
      <p className="dashboard-classic-card__value">{formatCount(value)}</p>
      {hint ? <p className="dashboard-classic-card__hint">{hint}</p> : null}
    </>
  );

  if (asLink && to) {
    return (
      <Link to={to} className="dashboard-classic-card__half dashboard-classic-card__half--top">
        {inner}
      </Link>
    );
  }

  return (
    <div className="dashboard-classic-card__half dashboard-classic-card__half--top">{inner}</div>
  );
}

function CardHalfBottom({ label, value, to, enabled }) {
  const inner = (
    <>
      <p className="dashboard-classic-card__title dashboard-classic-card__title--inverse">
        {label}
      </p>
      <p className="dashboard-classic-card__value dashboard-classic-card__value--inverse">
        {formatCount(value)}
      </p>
    </>
  );

  if (to && enabled) {
    return (
      <Link
        to={to}
        className="dashboard-classic-card__half dashboard-classic-card__half--bottom"
      >
        {inner}
      </Link>
    );
  }

  return (
    <div className="dashboard-classic-card__half dashboard-classic-card__half--bottom">
      {inner}
    </div>
  );
}

export function DashboardStatCardSkeleton() {
  return (
    <article
      className="dashboard-classic-card dashboard-classic-card--skeleton"
      aria-busy="true"
      aria-label="Loading statistic"
    >
      <div className="dashboard-classic-card__half dashboard-classic-card__half--top">
        <span className="dashboard-classic-card__sk-line" />
        <span className="dashboard-classic-card__sk-value" />
      </div>
      <div className="dashboard-classic-card__half dashboard-classic-card__half--bottom">
        <span className="dashboard-classic-card__sk-line dashboard-classic-card__sk-line--inverse" />
        <span className="dashboard-classic-card__sk-value dashboard-classic-card__sk-value--inverse" />
      </div>
    </article>
  );
}

export function DashboardStatCard({
  title,
  value,
  primaryTo,
  primaryEnabled = true,
  footerLabel = "Total",
  footerValue,
  footerTo,
  footerEnabled = true,
  hint,
  badgeNew = false,
}) {
  const bottomValue =
    footerValue !== undefined && footerValue !== "" ? footerValue : value;

  return (
    <article className="dashboard-classic-card relative">
      {badgeNew ? (
        <span className="dashboard-classic-card__badge-new">New</span>
      ) : null}
      <CardHalfTop
        title={title}
        value={value}
        hint={hint}
        asLink={!!(primaryTo && primaryEnabled)}
        to={primaryTo}
      />
      {footerLabel ? (
        <CardHalfBottom
          label={footerLabel}
          value={bottomValue}
          to={footerTo}
          enabled={footerEnabled}
        />
      ) : null}
    </article>
  );
}

export function DashboardDualStatCard({
  title,
  pending,
  completed,
  pendingTo,
  completedTo,
  hint,
}) {
  return (
    <article className="dashboard-classic-card">
      <CardHalfTop
        title={title}
        value={pending}
        subLabel="Pending"
        hint={hint}
        asLink
        to={pendingTo}
      />
      <CardHalfBottom
        label="Completed"
        value={completed}
        to={completedTo}
        enabled={true}
      />
    </article>
  );
}

export function DashboardExpandToggle({ expanded, onClick }) {
  return (
    <div className="mt-6 flex justify-center">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex min-w-[10rem] items-center justify-center gap-2 rounded-lg border border-[var(--dashboard-brand,#0b1e5a)] bg-[var(--dashboard-brand,#0b1e5a)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6b8fc7] active:scale-[0.98]"
      >
        <span>{expanded ? "Show less" : "Show more"}</span>
        <FaChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
    </div>
  );
}

export function DashboardPanel({ title, subtitle, children, className = "" }) {
  return (
    <section
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {(title || subtitle) && (
        <header className="border-b border-slate-100 px-4 py-3 sm:px-5">
          {title ? <h2 className="m-0 text-base font-bold text-slate-900">{title}</h2> : null}
          {subtitle ? (
            <p className="m-0 mt-0.5 text-[13px] text-slate-500">{subtitle}</p>
          ) : null}
        </header>
      )}
      {children}
    </section>
  );
}

export function DashboardSegmentTabs({ tabs, activeId, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-slate-100 p-3">
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              active
                ? "bg-[var(--dashboard-brand,#0b1e5a)] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
